import type { DesignBody } from '@repo/api-contracts';
import {
  type FacePreviewInput,
  fitWithinPixels,
  renderFacePreview,
  resizeRaster,
} from '@repo/design-renderer';
import type { Raster } from '@repo/warp-engine';
import { useCallback, useMemo } from 'react';

/**
 * 끄는 동안 다시 그리는 곡면 합성의 사진 픽셀 상한 — 한 번 그리기가 ≈33ms 안에 끝나도록 낮게 둔다.
 * 250,000 에서는 마스크·음영이 있는 격자 면이 p90 35ms 를 넘어(node 측정) 200,000 으로 낮췄다(p90 ≈27ms).
 */
export const DRAG_MAX_PIXELS = 200_000;

interface UseLiveWarpedFaceInput {
  /** 손을 뗀 상태에서 마지막으로 그린 렌더 입력(useWarpedPreview). 없으면 끄는 동안 다시 그리지 않는다. */
  readonly lastInput: FacePreviewInput | null;
  /** 스펙 대역용 — 기본은 렌더러. */
  readonly render?: typeof renderFacePreview;
}

export interface LiveWarpedFace {
  /** 본문만 바꿔 낮은 해상도로 같은 렌더러를 동기로 부른다. 입력이 없거나 렌더가 실패하면 null. */
  readonly renderLive: (body: DesignBody) => Raster | null;
}

const reduceTo = (raster: Raster | undefined, width: number, height: number) =>
  raster === undefined || (raster.width === width && raster.height === height)
    ? raster
    : resizeRaster(raster, width, height);

/**
 * 곡면 상품을 끄는 동안의 실시간 합성 — 마지막 렌더 입력의 사진·마스크·음영을 DRAG_MAX_PIXELS 로 한 번 줄여 두고,
 * 끌 때마다 본문만 바꿔 renderFacePreview 를 동기로 부른다. 결과는 사진 비율 그대로라 호출자가 사진 상자에 늘인다.
 */
export const useLiveWarpedFace = ({
  lastInput,
  render = renderFacePreview,
}: UseLiveWarpedFaceInput): LiveWarpedFace => {
  const photo = lastInput?.face.photo;
  const mask = lastInput?.face.mask;
  const shading = lastInput?.face.shading;
  // 사진·마스크·음영이 같으면 렌더 입력 객체가 새로 와도 다시 줄이지 않는다.
  const reduced = useMemo(() => {
    if (photo === undefined) return null;
    const fit = fitWithinPixels(photo.width, photo.height, DRAG_MAX_PIXELS);
    const small = {
      photo: reduceTo(photo, fit.width, fit.height) ?? photo,
      mask: reduceTo(mask, fit.width, fit.height),
      shading: reduceTo(shading, fit.width, fit.height),
    };
    return {
      photo: small.photo,
      ...(small.mask === undefined ? {} : { mask: small.mask }),
      ...(small.shading === undefined ? {} : { shading: small.shading }),
    };
  }, [photo, mask, shading]);

  const renderLive = useCallback(
    (body: DesignBody): Raster | null => {
      if (lastInput === null || reduced === null) return null;
      try {
        return render({ ...lastInput, body, face: reduced, maxPixels: DRAG_MAX_PIXELS }).raster;
      } catch {
        // 재료가 아직 없으면 이번 프레임은 건너뛴다(호출자가 마지막 그림을 유지한다).
        return null;
      }
    },
    [lastInput, reduced, render],
  );

  return { renderLive };
};
